import {
  createFormGroupState,
  setErrors,
  setUserDefinedProperty,
  updateGroup,
  validate,
} from 'ngrx-forms';
import { requiredFalse } from 'ngrx-forms/validation';
import {
  ConsentGroupType,
  ConsentStatus,
  ConsentType,
  type GetConsentGroupResponse,
  type GetConsentResponse,
} from '@integration/api-client/consent';

import { consentFormId } from '../+state/form/consent-form.reducer';
import type { ConsentForm, ConsentFormValues } from './consent-form.model';

export const consentMock: GetConsentResponse[] = [
  {
    consent_type: ConsentType.BASIC_INSIGHTS,
    title: 'Basic',
    description: 'Lorem ipsum',
    version_id: 1,
    status: ConsentStatus.GIVEN,
    inform: false,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.OPTIMAL_INSIGHTS,
    title: 'Optimal',
    description: 'Lorem ipsum',
    depends_on: ConsentType.BASIC_INSIGHTS,
    version_id: 2,
    status: ConsentStatus.GIVEN,
    inform: false,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.FORECASTING_AND_PURCHASING,
    title: 'Forecasting and purchasing',
    description: 'Lorem ipsum',
    version_id: 3,
    status: ConsentStatus.REVOKED,
    inform: false,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.RESEARCH_AND_DEVELOPMENT,
    title: 'Research and development',
    description: 'Lorem ipsum',
    version_id: 4,
    status: ConsentStatus.GIVEN,
    inform: false,
    readonly: true,
    updatable_by_customer: true,
  },
];

export const consentMockInitialStatus: GetConsentResponse[] = [
  {
    consent_type: ConsentType.BASIC_INSIGHTS,
    title: 'Basic',
    description: 'Lorem ipsum',
    version_id: 1,
    status: ConsentStatus.REVOKED,
    inform: true,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.OPTIMAL_INSIGHTS,
    title: 'Optimal',
    description: 'Lorem ipsum',
    depends_on: ConsentType.BASIC_INSIGHTS,
    version_id: 2,
    status: ConsentStatus.REVOKED,
    inform: true,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.FORECASTING_AND_PURCHASING,
    title: 'Forecasting and purchasing',
    description: 'Lorem ipsum',
    version_id: 3,
    status: ConsentStatus.REVOKED,
    inform: true,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.RESEARCH_AND_DEVELOPMENT,
    title: 'Research and development',
    description: 'Lorem ipsum',
    version_id: 4,
    status: ConsentStatus.GIVEN,
    inform: false,
    readonly: true,
    updatable_by_customer: true,
  },
];

export const consentMockAllGiven: GetConsentResponse[] = [
  {
    consent_type: ConsentType.BASIC_INSIGHTS,
    title: 'Basic',
    description: 'Lorem ipsum',
    version_id: 1,
    status: ConsentStatus.GIVEN,
    inform: false,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.OPTIMAL_INSIGHTS,
    title: 'Optimal',
    description: 'Lorem ipsum',
    depends_on: ConsentType.BASIC_INSIGHTS,
    version_id: 2,
    status: ConsentStatus.GIVEN,
    inform: false,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.FORECASTING_AND_PURCHASING,
    title: 'Forecasting and purchasing',
    description: 'Lorem ipsum',
    version_id: 3,
    status: ConsentStatus.GIVEN,
    inform: false,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.RESEARCH_AND_DEVELOPMENT,
    title: 'Research and development',
    description: 'Lorem ipsum',
    version_id: 4,
    status: ConsentStatus.GIVEN,
    inform: false,
    readonly: true,
    updatable_by_customer: true,
  },
];

export const consentMockAllRevoked: GetConsentResponse[] = [
  {
    consent_type: ConsentType.BASIC_INSIGHTS,
    title: 'Basic',
    description: 'Lorem ipsum',
    version_id: 1,
    status: ConsentStatus.REVOKED,
    inform: false,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.OPTIMAL_INSIGHTS,
    title: 'Optimal',
    description: 'Lorem ipsum',
    depends_on: ConsentType.BASIC_INSIGHTS,
    version_id: 2,
    status: ConsentStatus.REVOKED,
    inform: false,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.FORECASTING_AND_PURCHASING,
    title: 'Forecasting and purchasing',
    description: 'Lorem ipsum',
    version_id: 3,
    status: ConsentStatus.REVOKED,
    inform: false,
    readonly: false,
    updatable_by_customer: true,
  },
  {
    consent_type: ConsentType.RESEARCH_AND_DEVELOPMENT,
    title: 'Research and development',
    description: 'Lorem ipsum',
    version_id: 4,
    status: ConsentStatus.GIVEN,
    inform: false,
    readonly: true,
    updatable_by_customer: true,
  },
];

export const consentGroupMock: GetConsentGroupResponse[] = [
  {
    title: 'Metering',
    description: 'Lorem ipsum',
    type: ConsentGroupType.METERING,
    sub_groups: [{ consents: consentMock }],
  },
];

export const consentGroupAllGivenMock: GetConsentGroupResponse[] = [
  {
    ...consentGroupMock[0],
    sub_groups: [{ consents: consentMockAllGiven }],
  },
];

export const consentFormValuesMock: ConsentForm =
  createFormGroupState<ConsentFormValues>(consentFormId, {
    acceptAll: true,
    consents: {
      1: true,
      2: true,
      3: true,
    },
  });

export const consentFormMock = setUserDefinedProperty(
  'consentGroupType',
  ConsentGroupType.METERING
)(consentFormValuesMock);

export const consentFormValuesFalseMock: ConsentForm =
  createFormGroupState<ConsentFormValues>(consentFormId, {
    acceptAll: false,
    consents: {
      1: false,
      2: false,
      3: false,
    },
  });

export const consentFormFalseMock = setUserDefinedProperty(
  'consentGroupType',
  ConsentGroupType.METERING
)(consentFormValuesFalseMock);

export const consentInvalidFormMock = setErrors({ required: {} as any })(
  consentFormMock
);

export const consentFormDependsOnValuesMock: ConsentForm =
  createFormGroupState<ConsentFormValues>(consentFormId, {
    acceptAll: false,
    consents: {
      1: false,
      2: true,
      3: false,
    },
  });

export const consentDependsOnFormMock = setUserDefinedProperty(
  'consentGroupType',
  ConsentGroupType.METERING
)(consentFormValuesMock);

export const consentFormWithRequiredErrorMock: ConsentForm =
  updateGroup<ConsentFormValues>({
    consents: updateGroup<{ [key: number]: boolean }>({
      2: validate(requiredFalse),
    }),
  })(consentDependsOnFormMock);
